import { useState, lazy, Suspense } from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';

import { Navbar } from '../components/layout/Navbar';
import { SkipToContent } from '../components/SkipToContent';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { AIWidget } from '../components/ui/AIWidget';
import { SectionDivider } from '../components/ui/SectionDivider';
import { IndustryShowcase } from '../components/ui/IndustryShowcase';
import { ROICalculator } from '../components/ui/ROICalculator';
import { AudioSamples } from '../components/ui/AudioSamples';
import { Guarantee } from '../components/sections/Guarantee';
import { Integrations } from '../components/sections/Integrations';
import { analytics } from '../utils/analytics';

// Lazy load heavy components
const Services = lazy(() => import('../components/sections/Services').then(m => ({ default: m.Services })));
const Process = lazy(() => import('../components/sections/Process').then(m => ({ default: m.Process })));
const CaseStudies = lazy(() => import('../components/sections/CaseStudies').then(m => ({ default: m.CaseStudies })));
const About = lazy(() => import('../components/sections/About').then(m => ({ default: m.About })));
const BookingModal = lazy(() => import('../components/ui/BookingModal').then(m => ({ default: m.BookingModal })));
import { Hero } from '../components/sections/Hero';

export const Home = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const handleBookingModalOpen = () => {
    setIsBookingModalOpen(true);
    analytics.bookingModalOpened();
  };

  return (
    <div className="min-h-screen bg-brand-dark text-white selection:bg-brand-primary/30 font-sans">
      <SkipToContent />
      <Navbar />

      {/* Hero Section */}
      <main id="main-content">
        <Hero onBookCall={handleBookingModalOpen} />

      {/* Problem Section */}
      <SectionDivider icon={AlertTriangle} label="The Problem" gradient="from-red-500/20 to-transparent" />
      <section className="py-20 bg-brand-base/50 border-y border-brand-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6 font-display text-white">
              You're Losing Money <span className="text-red-500">Every Day</span> to These Problems:
            </h2>
            <div className="bg-brand-dark/50 border border-brand-border rounded-2xl p-8 max-w-2xl mx-auto text-left shadow-2xl">
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <span className="text-2xl">❌</span>
                  <span className="text-lg text-slate-300 font-medium pt-1">Calls going to voicemail during busy times</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">❌</span>
                  <span className="text-lg text-slate-300 font-medium pt-1">After-hours inquiries going to competitors</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">❌</span>
                  <span className="text-lg text-slate-300 font-medium pt-1">Customers who won't leave voicemails</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">❌</span>
                  <span className="text-lg text-slate-300 font-medium pt-1">Staff overwhelmed juggling phones and customers</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">❌</span>
                  <span className="text-lg text-slate-300 font-medium pt-1">You personally fielding calls on weekends</span>
                </li>
              </ul>
              <div className="text-center pt-6 border-t border-brand-border/50">
                <p className="text-xl font-bold text-white">
                  Every missed connection is <span className="text-brand-accent">money walking out the door.</span>
                </p>
              </div>
            </div>
            
            {/* Assessment CTA */}
            <div className="mt-12">
               <button 
                onClick={handleBookingModalOpen}
                className="inline-flex items-center text-lg font-bold text-brand-primary hover:text-brand-accent transition-colors group"
               >
                 Calculate Your Emergency Call Revenue Loss
                 <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
               </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <Suspense fallback={<LoadingSkeleton type="services" />}>
        <Services onBookCall={handleBookingModalOpen} />
      </Suspense>

      {/* Audio Samples Mid-Page CTA */}
      <AudioSamples />

      {/* ROI Calculator - HIGH IMPACT CONVERSION */}
      <ROICalculator onBookCall={handleBookingModalOpen} />

      {/* Industry Diversity Showcase */}
      <IndustryShowcase />

      {/* Process Section */}
      <Suspense fallback={<LoadingSkeleton type="process" />}>
        <Integrations />
        <Process onBookCall={handleBookingModalOpen} />
      </Suspense>

      {/* Guarantee Section */}
      <Guarantee />

      {/* Case Studies Section */}
      <Suspense fallback={<LoadingSkeleton type="caseStudies" />}>
        <CaseStudies onBookCall={handleBookingModalOpen} />
      </Suspense>

      {/* About Section */}
      <Suspense fallback={<LoadingSkeleton type="services" />}>
        <About />
      </Suspense>

      <Suspense fallback={null}>
        <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
      </Suspense>
      </main>

      {/* AI Chat Widget */}
      <AIWidget />
    </div>
  );
};
